import { useState } from 'react'
import { Highlight, Language } from 'prism-react-renderer'
import Section from 'components/common/Section'
import { SectionHeader } from 'components/marketing/SectionHeader'
import { Button } from 'designsystem/Button'
import { ComponentTabsWithCode } from 'types/generated/contentful'
import { useThemeColor } from './ThemeContext'
import cn from 'classnames'
import CopyIcon from 'icons/CopyIcon'
import ExternalIcon from 'icons/ExternalIcon'
import { useCopyToClipboard } from '@stellate/react-utils'
import { stellateTheme } from 'styles/prism-react-renderer-theme'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

export const TabsWithCode = ({
  label,
  headline,
  subheadline,
  externalLinkLabel,
  externalLinkUrl,
  windowTitle,
  tabsCodeCollection,
}: ComponentTabsWithCode): JSX.Element => {
  const themeColor = useThemeColor()
  const [isCopied, copy] = useCopyToClipboard()
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const activeTab = tabsCodeCollection?.items[activeTabIndex]

  return (
    <Section bgColor='gray'>
      <SectionHeader
        label={label!}
        title={headline!}
        subline={subheadline!}
        highlight={themeColor}
      />

      <div className='lg:hidden'>
        <div className='neutralize-horizontal-padding'>
          <div className='flex gap-2 overflow-x-auto px-8 scrollbar-none'>
            {tabsCodeCollection?.items.map((tabCode, index) => (
              <button
                key={index}
                className={cn(
                  'flex-shrink-0 text-15 font-demibold px-3 py-2 border border-neutral-10 rounded-12',
                  {
                    'bg-blue-7 border-blue-25': activeTabIndex === index,
                  },
                )}
                onClick={() => {
                  setActiveTabIndex(index)
                }}
              >
                {tabCode?.title}
              </button>
            ))}
          </div>
        </div>
        <div className='mt-6'>
          <CodeWindow
            windowTitle={windowTitle!}
            codeBlock={activeTab?.codeBlock as any}
          />
        </div>
        <p className='mt-4 text-15 text-neutral-60'>{activeTab?.description}</p>
        <div className='mt-4 flex gap-2'>
          <Button
            small
            secondary
            pageType='marketing'
            className='gap-2.5'
            onClick={() => {
              window.open(externalLinkUrl!, '_blank')
            }}
          >
            {externalLinkLabel}
            <ExternalIcon />
          </Button>
          <Button
            small
            secondary
            pageType='marketing'
            onClick={() => {
              copy(activeTab!.codeBlock!.code!)
            }}
            className='gap-2.5'
          >
            <CopyIcon />
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>

      <div className='hidden lg:grid lg:grid-cols-12 lg:gap-8'>
        <div className='col-span-5 flex flex-col gap-4'>
          {tabsCodeCollection?.items.map((tabCode, index) => (
            <button
              key={index}
              className={cn(
                'px-6 py-5 text-left rounded-12 hover:bg-gradient-white-to-gray hover:shadow-small',
                {
                  'shadow-small bg-gradient-white-to-gray':
                    activeTabIndex === index,
                },
              )}
              onClick={() => {
                setActiveTabIndex(index)
              }}
            >
              <div className='flex items-center justify-between gap-2'>
                <div className='text-18 font-demibold'>{tabCode?.title}</div>
                {tabCode?.badge && (
                  <span className='px-1.5 py-px border border-neutral-25 rounded font-demibold text-12 whitespace-nowrap'>
                    {tabCode?.badge}
                  </span>
                )}
              </div>
              <div
                className={cn(
                  'mt-3 text-15 font-medium text-neutral-60 simple-rich-text',
                  `theme-${themeColor}`,
                )}
              >
                {documentToReactComponents(tabCode!.text!.json)}
              </div>
            </button>
          ))}
        </div>
        <div className='col-start-7 col-span-6'>
          <div className='sticky top-8'>
            <CodeWindow
              windowTitle={windowTitle!}
              codeBlock={activeTab?.codeBlock as any}
            />
            <div className='absolute bottom-5 right-6 flex gap-2'>
              <Button
                small
                pageType='marketing'
                className='gap-2.5 !bg-neutral-25 !text-neutral-on-dark-60'
                onClick={() => {
                  window.open(externalLinkUrl!, '_blank')
                }}
              >
                {externalLinkLabel}
                <ExternalIcon />
              </Button>
              <Button
                small
                pageType='marketing'
                className='gap-2.5 !bg-neutral-25 !text-neutral-on-dark-60'
                onClick={() => {
                  copy(activeTab!.codeBlock!.code!)
                }}
              >
                <CopyIcon />
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

function CodeWindow({
  windowTitle,
  codeBlock,
}: {
  windowTitle: string
  codeBlock: { language: string; code: string }
}) {
  return (
    <div>
      <div className='bg-[#1B283E] rounded-t-8 h-6 flex items-center justify-center relative lg:h-7'>
        <div className='absolute top-2 left-3 flex gap-1'>
          <div className='h-2 w-2 bg-neutral rounded-full' />
          <div className='h-2 w-2 bg-neutral rounded-full' />
          <div className='h-2 w-2 bg-neutral rounded-full' />
        </div>
        <div className='font-mono text-12 text-neutral-on-dark-15'>
          {windowTitle}
        </div>
      </div>
      <div
        className='rounded-b-8 overflow-hidden'
        style={{
          background: 'linear-gradient(180deg, #293B5B 0%, #0C1F40 100%)',
        }}
      >
        <Highlight
          theme={stellateTheme}
          code={codeBlock.code.trim()}
          language={(codeBlock.language as Language) || 'jsx'}
        >
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <pre
              className={`overflow-auto p-4 text-12 !bg-transparent lg:text-15 ${
                className || ''
              }`}
              style={{ ...style }}
            >
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line, key: i })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
      </div>
    </div>
  )
}
